import { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import jsCookie from "js-cookie";
import { sendSubscribeEvent } from "lib/analytics";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Title from "components/shared/stays/Title";
import Text from "components/shared/stays/Text";
import Button from "solar/components/Button";
import TextInputWithLabel from "solar/components/TextInputWithLabel";
import FormNote from "solar/components/FormNote";
import Alert from "solar/components/Alert";
import styles from "components/giveaways/EmailSignup.module.scss";

export default function EmailSignup({
  formLocation,
  subscribeEvent,
  listId,
  giveawayName,
}) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [subscribed, setSubscribed] = useState(false);

  const schema = Yup.object().shape({
    email: Yup.string()
      .required("Your email is required.")
      .email("A valid email is required."),
    fname: Yup.string().required("Your first name is required."),
    lname: Yup.string().required("Your last name is required."),
  });
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  async function submitForm(data) {
    try {
      const utmSource = jsCookie.get("utm-source");

      if (utmSource !== undefined) {
        data.utmSource = utmSource; // eslint-disable-line no-param-reassign
      }

      if (listId !== undefined) {
        data.listId = listId; // eslint-disable-line no-param-reassign
      }

      if (giveawayName !== undefined) {
        data.giveawayName = giveawayName; // eslint-disable-line no-param-reassign
      }

      data.subscribeFormLocation = formLocation; // eslint-disable-line no-param-reassign

      await axios.post("/api/subscribe", data);
      setSubscribed(true);
      sendSubscribeEvent(subscribeEvent);
    } catch (error) {
      setErrorMessage(error.response.data.errorMsg);
    }
  }

  return (
    <div className={styles.component}>
      {subscribed ? (
        <>
          <Title className={styles.title} size={5} tag="span">
            Success!
          </Title>
          <Text className={styles.text}>
            You&apos;re signed up. On to the next step.
          </Text>
        </>
      ) : (
        <>
          {errorMessage && (
            <Alert type="warning" title="Let's try that again">
              {errorMessage}
            </Alert>
          )}
          <form
            className={styles.form}
            onSubmit={handleSubmit(submitForm)}
            noValidate
          >
            <TextInputWithLabel
              className={styles.input}
              id="fname"
              type="text"
              name="fname"
              register={register}
              placeholder="First name"
              block
              labelText="First name"
            />
            {errors.fname && (
              <FormNote hasError>{errors.fname.message}</FormNote>
            )}
            <TextInputWithLabel
              className={styles.input}
              id="lname"
              type="text"
              name="lname"
              register={register}
              placeholder="Last name"
              block
              labelText="Last name"
            />
            {errors.lname && (
              <FormNote hasError>{errors.lname.message}</FormNote>
            )}
            <TextInputWithLabel
              className={styles.input}
              id="email"
              type="email"
              name="email"
              register={register}
              placeholder="Email address"
              block
              labelText="Email"
            />
            {errors.email && (
              <FormNote hasError>{errors.email.message}</FormNote>
            )}
            <Button
              size={2}
              type="submit"
              isSubmitting={isSubmitting}
              block
              className={styles.action}
              heap="subscribe"
            >
              Subscribe
            </Button>
          </form>
        </>
      )}
    </div>
  );
}

EmailSignup.propTypes = {
  formLocation: PropTypes.string.isRequired,
  subscribeEvent: PropTypes.string.isRequired,
  listId: PropTypes.string,
  giveawayName: PropTypes.string,
};
